import { useCallback, useEffect, useState } from "react";
import { api, mesajEroare } from "./api.js";
import { um } from "./um.js";
import { data } from "./text.js";
import { CATEGORII_INTERVENTIE, STARI_VIZITA, eticheta } from "./enums.js";
import { tr } from "./i18n.js";

const STARI = {
  Ciorna: "Ciornă", AsteaptaAprobare: "Așteaptă aprobare",
  Trimisa: "Trimisă", Vizualizata: "Deschisă de client",
  Acceptata: "Acceptată", Refuzata: "Refuzată", Expirata: "Expirată",
};

const EMITERE = ["ADMIN_TENANT", "MANAGER_TEHNIC", "DISPECER", "COORDONATOR", "FINANCIAR"];

/** Banii se scriu cu virgulă zecimală, ca peste tot în aplicație. */
const lei = (v, moneda = "RON") =>
  v == null ? "—" : `${Number(v).toLocaleString("ro-RO", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })} ${moneda}`;


export default function Oferte({ eu }) {
  const [lista, setLista] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [clientId, setClientId] = useState("");
  const [deschisa, setDeschisa] = useState(null);
  const [ciornaNoua, setCiornaNoua] = useState(false);
  const [link, setLink] = useState(null);
  const [incarca, setIncarca] = useState(true);
  const [eroare, setEroare] = useState("");

  const poateEmite = EMITERE.includes(eu.rol);
  /*
   * Dreptul de aprobare se ÎNTREABĂ, nu se deduce dintr-o listă de roluri
   * scrisă aici. Ăsta e chiar rostul matricei: o firmă poate muta aprobarea pe
   * coordonator, iar butonul trebuie să se mute odată cu ea. O listă în
   * interfață ar face ca serverul să accepte ce ecranul nu arată.
   *
   * `null` = „încă nu știu", și NU e „am voie": până răspunde serverul,
   * butoanele lipsesc. Lecție plătită la ecranul „Azi", unde `true` pe
   * necunoscut trimitea cereri care se întorceau 403.
   */
  const [poateAproba, setPoateAproba] = useState(null);

  useEffect(() => {
    api.drepturileMele()
      .then((d) => setPoateAproba((d?.oferte ?? []).includes("aprobare")))
      .catch(() => setPoateAproba(false));
  }, []);

  const reincarca = useCallback(async () => {
    setIncarca(true);
    try { setLista(await api.oferte({ clientId: clientId || undefined })); }
    catch (e) { setEroare(mesajEroare(e)); }
    finally { setIncarca(false); }
  }, [clientId]);

  useEffect(() => { reincarca(); }, [reincarca]);
  useEffect(() => { api.clienti().then(setClienti).catch(() => {}); }, []);

  return (
    <div className="continut">
      <h1>{tr("Oferte")}</h1>
      <p className="subtitlu">
        {tr("Prețul se construiește din catalog, cu manopera pe norma din bibliotecă — o estimare, marcată ca atare. La trimitere conținutul se îngheață: o listă de prețuri schimbată luna viitoare nu mai rescrie o ofertă deja făcută.")}
      </p>

      {eroare && <div className="eroare">{eroare}</div>}

      <div className="panou">
        <div className="panou-corp">
          <div className="linie-actiune" style={{ alignItems: "center" }}>
            <label>{tr("Client")}
              <select className="camp" value={clientId} onChange={(e) => setClientId(e.target.value)}>
                <option value="">{tr("toți clienții")}</option>
                {clienti.map((c) => <option key={c.id} value={c.id}>{tr(c.denumire)}</option>)}
              </select>
            </label>
            {poateEmite && (
              <button className="buton buton-actiune" onClick={() => setCiornaNoua(true)}>
                {tr("ofertă nouă")}
              </button>
            )}
          </div>
        </div>
      </div>

      {poateAproba === true && <PragAprobare />}

      {incarca ? (
        <div className="gol" style={{ padding: "2rem 0" }}>{tr("Se încarcă…")}</div>
      ) : lista.length === 0 ? (
        <div className="panou"><div className="panou-corp gol">{tr("Nicio ofertă.")}</div></div>
      ) : (
        <div className="panou">
          <table className="tabel tabel-catalog">
            <thead>
              <tr>
                <th>{tr("Număr")}</th><th>{tr("Titlu")}</th><th>{tr("Client")}</th><th className="num">{tr("Total")}</th>
                <th>{tr("Stare")}</th><th>{tr("Valabilă până la")}</th><th />
              </tr>
            </thead>
            <tbody>
              {lista.map((o) => (
                <tr key={o.id}>
                  <td><code>{o.numar}</code></td>
                  <td>{tr(o.titlu)}</td>
                  <td>{tr(o.client?.denumire)}</td>
                  <td className="num">{o.status === "Ciorna" ? "—" : lei(o.total, o.moneda)}</td>
                  <td>
                    {tr(STARI[o.status] ?? o.status)}
                    {o.expirata && <span className="marcaj">{tr("expirată")}</span>}
                  </td>
                  <td>{data(o.valabilPanaLa)}</td>
                  <td className="celula-actiuni">
                    <button className="buton buton-mic buton-discret"
                      onClick={async () => {
                        try { setDeschisa(await api.oferta(o.id)); }
                        catch (e) { setEroare(mesajEroare(e)); }
                      }}>
                      {tr("deschide")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {ciornaNoua && (
        <CiornaNoua
          clienti={clienti}
          laInchidere={() => setCiornaNoua(false)}
          laSalvare={async (o) => { setCiornaNoua(false); await reincarca(); setDeschisa(o); }}
        />
      )}

      {deschisa && (
        <Detaliu
          oferta={deschisa} poateEmite={poateEmite} poateAproba={poateAproba === true}
          laInchidere={() => setDeschisa(null)}
          laLink={setLink}
          laSchimbare={async () => {
            setDeschisa(await api.oferta(deschisa.id));
            await reincarca();
          }}
        />
      )}

      {link && <Link link={link} laInchidere={() => setLink(null)} />}
    </div>
  );
}

// ── Linkul, arătat o singură dată ──────────────────────────────────────────

/**
 * Bara peste care o ofertă cere aprobare.
 *
 * O așază cine poate aproba, nu cine emite: altfel dispecerul care construiește
 * oferta ar putea ridica pragul peste care nu mai trece nimic niciodată, iar
 * poarta ar rămâne desenată pe hârtie.
 */
function PragAprobare() {
  const [stare, setStare] = useState(null);
  const [suma, setSuma] = useState("");
  const [moneda, setMoneda] = useState("");
  const [deschis, setDeschis] = useState(false);
  const [lucreaza, setLucreaza] = useState(false);
  const [eroare, setEroare] = useState("");

  const citeste = useCallback(async () => {
    try {
      const r = await api.pragAprobare();
      setStare(r);
      setSuma(r.prag === null ? "" : String(r.prag));
      setMoneda(r.moneda ?? r.monedaFirmei ?? "");
    } catch (e) { setEroare(mesajEroare(e)); }
  }, []);
  useEffect(() => { citeste(); }, [citeste]);

  async function salveaza(prag) {
    setLucreaza(true); setEroare("");
    try {
      await api.setPragAprobare(prag === null
        ? { prag: null }
        : { prag, ...(moneda ? { moneda } : {}) });
      await citeste();
      setDeschis(false);
    } catch (e) { setEroare(mesajEroare(e)); }
    finally { setLucreaza(false); }
  }

  if (!stare) return null;

  return (
    <div className="panou">
      <div className="panou-antet">
        <span>{tr("Prag de aprobare")}</span>
        <button className="buton buton-mic buton-sters" onClick={() => setDeschis(!deschis)}>
          {deschis ? tr("închide") : tr("schimbă")}
        </button>
      </div>
      <div className="panou-corp">
        {eroare && <div className="eroare">{eroare}</div>}
        <p className="nota-cautare" style={{ marginTop: 0 }}>
          {stare.activ
            ? tr("Ofertele peste {0} {1} nu pleacă la client până nu le aprobă cineva cu dreptul.", stare.prag, stare.moneda)
            : tr("Nu e pus niciun prag: ofertele pleacă direct la client, ca până acum.")}
        </p>
        {deschis && (
          <div className="linie-actiune" style={{ alignItems: "center" }}>
            <label>{tr("Peste suma")}
              <input className="camp" type="number" min={0} step="0.01" value={suma}
                style={{ minWidth: "9rem", width: "9rem" }}
                onChange={(e) => setSuma(e.target.value)} />
            </label>
            <label>{tr("Moneda")}
              <input className="camp" value={moneda} maxLength={3} placeholder="RON"
                style={{ minWidth: "6rem", width: "6rem" }}
                onChange={(e) => setMoneda(e.target.value.toUpperCase())} />
            </label>
            <button className="buton buton-mic buton-actiune"
              disabled={lucreaza || suma === "" || moneda.length !== 3}
              onClick={() => salveaza(Number(suma))}>
              {tr("pune pragul")}
            </button>
            {stare.activ && (
              <button className="buton buton-mic buton-sters" disabled={lucreaza}
                onClick={() => salveaza(null)}>
                {tr("scoate pragul")}
              </button>
            )}
          </div>
        )}
        {deschis && (
          <p className="nota">
            {tr("Zero înseamnă că ORICE ofertă trece pe la aprobare. O ofertă în altă monedă decât pragul nu se poate compara, deci merge tot la aprobare — aplicația nu inventează niciun curs.")}
          </p>
        )}
      </div>
    </div>
  );
}

function Link({ link, laInchidere }) {
  const [copiat, setCopiat] = useState(false);
  const adresa = `${window.location.origin}/oferta/${link}`;
  return (
    <div className="modal-fundal" onClick={laInchidere}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>{tr("Linkul de acceptare")}</h3>
        <div className="panou panou-atentie"><div className="panou-corp">
          <strong>{tr("Se vede o singură dată, acum.")}</strong> {tr("În baza de date rămâne doar amprenta lui, nu linkul — deci nu se mai poate afla de nicăieri. Dacă se pierde, retrimite oferta: se emite altul, iar acesta încetează să meargă.")}
        </div></div>
        <input className="camp" readOnly value={adresa} onFocus={(e) => e.target.select()} />
        <div className="modal-actiuni">
          <button className="buton buton-actiune"
            onClick={async () => {
              try { await navigator.clipboard.writeText(adresa); setCopiat(true); }
              catch { setCopiat(false); }
            }}>
            {copiat ? tr("copiat") : tr("copiază linkul")}
          </button>
          <button className="buton buton-sters" onClick={laInchidere}>{tr("am notat")}</button>
        </div>
      </div>
    </div>
  );
}

// ── Ciornă nouă ────────────────────────────────────────────────────────────

function CiornaNoua({ clienti, laInchidere, laSalvare }) {
  const [clientId, setClientId] = useState("");
  const [titlu, setTitlu] = useState("");
  const [descriere, setDescriere] = useState("");
  const [lucreaza, setLucreaza] = useState(false);
  const [eroare, setEroare] = useState("");

  return (
    <div className="modal-fundal" onClick={laInchidere}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>{tr("Ofertă nouă")}</h3>
        {eroare && <div className="eroare">{eroare}</div>}
        <label className="camp">{tr("Client")}
          <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="">{tr("— alege —")}</option>
            {clienti.map((c) => <option key={c.id} value={c.id}>{tr(c.denumire)}</option>)}
          </select>
        </label>
        <label className="camp">{tr("Titlu")}
          <input value={titlu} onChange={(e) => setTitlu(e.target.value)}
            placeholder={tr("Ce se ofertează")} />
        </label>
        <label className="camp">{tr("Descriere")}
          <textarea className="camp-textarea" rows={4} value={descriere}
            onChange={(e) => setDescriere(e.target.value)} />
        </label>
        <div className="modal-actiuni">
          <button className="buton buton-sters" onClick={laInchidere}>{tr("renunță")}</button>
          <button className="buton buton-actiune"
            disabled={lucreaza || !clientId || titlu.trim().length < 5}
            onClick={async () => {
              setLucreaza(true); setEroare("");
              try {
                laSalvare(await api.creeazaOferta({
                  clientId, titlu: titlu.trim(),
                  ...(descriere.trim() ? { descriere: descriere.trim() } : {}),
                }));
              } catch (e) { setEroare(mesajEroare(e)); setLucreaza(false); }
            }}>
            {lucreaza ? tr("se creează…") : tr("creează ciorna")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Detaliul ofertei ───────────────────────────────────────────────────────

function Detaliu({ oferta, poateEmite, poateAproba, laInchidere, laSchimbare, laLink }) {
  const [operatii, setOperatii] = useState([]);
  const [articole, setArticole] = useState([]);
  const [lucreaza, setLucreaza] = useState(false);
  const [eroare, setEroare] = useState("");
  const [zile, setZile] = useState(30);
  const [programez, setProgramez] = useState(false);
  const [motivRespingere, setMotivRespingere] = useState("");
  const [respingCu, setRespingCu] = useState(false);
  const asteapta = oferta.status === "AsteaptaAprobare";

  const ciorna = oferta.status === "Ciorna";
  const snap = oferta.liniiSnapshot;

  useEffect(() => {
    if (!ciorna) return;
    api.operatii().then((l) => setOperatii(l.slice(0, 500))).catch(() => {});
    api.articoleLista().then(setArticole).catch(() => {});
  }, [ciorna]);

  const liniiCurente = (oferta.linii ?? []).map((l) => ({
    ...(l.operatieId ? { operatieId: l.operatieId } : { articolId: l.articolId }),
    cantitate: Number(l.cantitate),
  }));

  async function scrieLinii(linii) {
    setLucreaza(true); setEroare("");
    try { await api.actualizeazaOferta(oferta.id, { linii }); await laSchimbare(); }
    catch (e) { setEroare(mesajEroare(e)); }
    finally { setLucreaza(false); }
  }

  return (
    <div className="modal-fundal" onClick={laInchidere}>
      <div className="modal modal-lat" onClick={(e) => e.stopPropagation()}>
        <h3>
          <code>{oferta.numar}</code> · {tr(oferta.titlu)}
          <span className="eticheta" style={{ marginLeft: ".5rem" }}>
            {tr(STARI[oferta.status] ?? oferta.status)}
          </span>
        </h3>
        <p className="subtitlu">
          {tr(oferta.client?.denumire)}
          {oferta.inlocuieste && ` · înlocuiește ${oferta.inlocuieste.numar}`}
          {oferta.inlocuitaDe && ` · înlocuită de ${oferta.inlocuitaDe.numar}`}
        </p>

        {eroare && <div className="eroare">{eroare}</div>}

        {/*
          Oferta stă. Panoul spune DE CE stă și cine are mutarea — altfel cel
          care a apăsat „trimite" ar crede că a plecat, iar cel care poate
          aproba n-ar ști că are ceva de făcut.
        */}
        {asteapta && (
          <div className="panou panou-atentie">
            <div className="panou-antet"><span>{tr("Așteaptă aprobare")}</span></div>
            <div className="panou-corp">
              <p style={{ marginTop: 0 }}>
                {tr("Oferta trece de pragul firmei, deci nu pleacă la client până nu o aprobă cineva cu dreptul. Conținutul e deja înghețat — nu se mai poate schimba până la o decizie.")}
              </p>
              {poateAproba ? (
                respingCu ? (
                  <div className="camp">
                    <label htmlFor="o-motiv">{tr("De ce o respingi")}</label>
                    <textarea
                      id="o-motiv" value={motivRespingere} rows={2}
                      placeholder={tr("ex. marja e prea mică pe manoperă, reface devizul")}
                      onChange={(e) => setMotivRespingere(e.target.value)}
                    />
                    <div className="linie-actiune">
                      <button
                        className="buton buton-mic buton-sters"
                        disabled={lucreaza || motivRespingere.trim().length < 5}
                        onClick={async () => {
                          setLucreaza(true); setEroare("");
                          try {
                            await api.respingeOferta(oferta.id, motivRespingere.trim());
                            setRespingCu(false); setMotivRespingere("");
                            await laSchimbare();
                          } catch (e) { setEroare(mesajEroare(e)); }
                          finally { setLucreaza(false); }
                        }}
                      >
                        {tr("trimite respingerea")}
                      </button>
                      <button className="buton buton-mic buton-discret"
                        onClick={() => { setRespingCu(false); setMotivRespingere(""); }}>
                        {tr("renunță")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="linie-actiune">
                    <button
                      className="buton buton-mic buton-actiune" disabled={lucreaza}
                      onClick={async () => {
                        setLucreaza(true); setEroare("");
                        try {
                          // Aprobarea DUCE mai departe trimiterea cerută, deci
                          // răspunsul poartă chiar linkul public.
                          const r = await api.aprobaOferta(oferta.id);
                          if (r?.token) laLink(r.token);
                          await laSchimbare();
                        } catch (e) { setEroare(mesajEroare(e)); }
                        finally { setLucreaza(false); }
                      }}
                    >
                      {tr("aprobă și trimite la client")}
                    </button>
                    <button className="buton buton-mic buton-sters" disabled={lucreaza}
                      onClick={() => setRespingCu(true)}>
                      {tr("respinge")}
                    </button>
                  </div>
                )
              ) : (
                <p className="nota" style={{ marginBottom: 0 }}>
                  {tr("Funcția ta nu aprobă oferte. A fost anunțat cine o poate face.")}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Oferta s-a întors în ciornă: motivul se vede pe ea, nu doar în jurnal. */}
        {ciorna && oferta.motivRespingere && (
          <div className="panou panou-atentie"><div className="panou-corp">
            <strong>{tr("Respinsă la aprobare.")}</strong>{" "}
            {tr("Motiv:")} {oferta.motivRespingere}
          </div></div>
        )}

        {oferta.decisLa && (
          <div className="panou panou-atentie"><div className="panou-corp">
            <strong>{oferta.status === "Acceptata" ? tr("Acceptată") : tr("Refuzată")}</strong> {tr("de")}{" "}
            {oferta.decisDeNume}{oferta.decisDeEmail ? ` (${oferta.decisDeEmail})` : ""} {tr("la")}{" "}
            {new Date(oferta.decisLa).toLocaleString("ro-RO")}.
            {oferta.motivRefuz && <div>{tr("Motiv:")} {oferta.motivRefuz}</div>}
          </div></div>
        )}

        {/*
          O ofertă acceptată se oprea aici. Aplicația NU naște vizita singură la
          acceptare — n-are nicio dată agreată, iar una inventată ar purta un
          termen pe care nu l-a promis nimeni — dar lucrarea nu mai are voie să
          dispară din vedere. De aici se programează, cu o dată aleasă de om.
        */}
        {oferta.status === "Acceptata" && (
          <div className="panou">
            <div className="panou-antet">
              <span>{tr("Lucrarea")}</span>
              {(oferta.vizite ?? []).length === 0 && (
                <button className="buton buton-mic buton-actiune" onClick={() => setProgramez(true)}>
                  {tr("programează lucrarea")}
                </button>
              )}
            </div>
            <div className="panou-corp">
              {(oferta.vizite ?? []).length === 0 ? (
                <div className="gol">
                  {tr("Clientul a acceptat, dar lucrarea n-a fost încă programată.")}
                </div>
              ) : (
                <ul className="lista-rezultate">
                  {oferta.vizite.map((v) => (
                    <li key={v.id}>
                      <span className="cod-mic">{v.numar}</span>
                      <span className="rezultat-context">
                        {data(v.dataProgramata)} · {eticheta(STARI_VIZITA, v.status)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {programez && (
          <ProgrameazaLucrarea
            oferta={oferta}
            laInchidere={() => setProgramez(false)}
            laSalvare={async () => { setProgramez(false); await laSchimbare(); }}
          />
        )}

        {ciorna ? (
          <>
            <div className="panou">
              <div className="panou-antet">{tr("Liniile ofertei")}</div>
              <div className="panou-corp">
                {(oferta.linii ?? []).length === 0 && (
                  <div className="gol">{tr("Nicio linie. O ofertă goală n-are ce să propună.")}</div>
                )}
                <table className="tabel tabel-catalog">
                  <thead>
                    <tr><th>{tr("Poziție")}</th><th className="num">{tr("Cantitate")}</th><th /></tr>
                  </thead>
                  <tbody>
                    {(oferta.linii ?? []).map((l, i) => (
                      <tr key={l.id}>
                        <td>
                          {l.operatie
                            ? <>{tr("manoperă ·")} {tr(l.operatie.denumire)} <span className="cod-mic">{l.operatie.cod}</span></>
                            : <>{tr("piesă ·")} {tr(l.articol?.denumire)} <span className="cod-mic">{l.articol?.cod}</span></>}
                        </td>
                        <td className="num">{Number(l.cantitate)}</td>
                        <td className="celula-actiuni">
                          <button className="buton buton-mic buton-sters" disabled={lucreaza}
                            onClick={() => scrieLinii(liniiCurente.filter((_, j) => j !== i))}>
                            {tr("scoate")}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <AdaugaLinie
                  operatii={operatii} articole={articole} lucreaza={lucreaza}
                  laAdaugare={(linie) => scrieLinii([...liniiCurente, linie])}
                />
              </div>
            </div>

            {poateEmite && (
              <div className="panou">
                <div className="panou-antet">{tr("Trimitere")}</div>
                <div className="panou-corp">
                  <div className="linie-actiune" style={{ alignItems: "center" }}>
                    <label>{tr("Valabilă (zile)")}
                      <input className="camp" style={{ minWidth: "7rem", width: "7rem" }}
                        type="number" min={1} value={zile}
                        onChange={(e) => setZile(Number(e.target.value))} />
                    </label>
                    <button className="buton buton-actiune"
                      disabled={lucreaza || (oferta.linii ?? []).length === 0}
                      onClick={async () => {
                        setLucreaza(true); setEroare("");
                        try {
                          const r = await api.trimiteOferta(oferta.id, { zileValabilitate: zile });
                          // Peste pragul firmei, oferta NU pleacă: nu există
                          // niciun link de arătat, iar o fereastră cu „null" ar
                          // fi mai rea decât niciuna.
                          if (r.token) laLink(r.token);
                          await laSchimbare();
                        } catch (e) { setEroare(mesajEroare(e)); }
                        finally { setLucreaza(false); }
                      }}>
                      {tr("calculează și trimite")}
                    </button>
                  </div>
                  <p className="nota-cautare">
                    {tr("La trimitere, prețurile se citesc din catalog și se îngheață pe ofertă. Manopera se calculează pe norma din bibliotecă: e o estimare, nu o măsurătoare.")}
                  </p>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="panou">
            <div className="panou-antet">{tr("Ce s-a trimis")}</div>
            <div className="panou-corp">
              <table className="tabel tabel-catalog">
                <thead>
                  <tr>
                    <th>{tr("Poziție")}</th><th className="num">{tr("Cantitate")}</th><th>UM</th>
                    <th className="num">{tr("Preț unitar")}</th><th className="num">{tr("Valoare")}</th><th className="num">TVA</th>
                  </tr>
                </thead>
                <tbody>
                  {(snap?.linii ?? []).map((l, i) => (
                    <tr key={i} className={l.dinNorma ? "linie-estimata" : ""}>
                      <td>
                        <div>{tr(l.denumire)} <span className="cod-mic">{l.cod}</span></div>
                        {l.dinNorma && <span className="fara-pret">{tr("din normă — estimare")}</span>}
                      </td>
                      <td className="num">{Number(l.cantitate).toLocaleString("ro-RO")}</td>
                      <td>{um(l.unitate)}</td>
                      <td className="num">{lei(l.pretUnitar, snap?.moneda)}</td>
                      <td className="num">{lei(l.valoare, snap?.moneda)}</td>
                      <td className="num">{l.cotaTVA}%</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr><td colSpan={4} /><td className="num">{lei(snap?.subtotal, snap?.moneda)}</td><td>{tr("fără TVA")}</td></tr>
                  <tr><td colSpan={4} /><td className="num">{lei(snap?.tva, snap?.moneda)}</td><td>TVA</td></tr>
                  <tr><td colSpan={4} /><td className="num"><strong>{lei(snap?.total, snap?.moneda)}</strong></td><td>{tr("total")}</td></tr>
                </tfoot>
              </table>
              <p className="nota-cautare">
                {tr("Amprenta conținutului:")} <code>{String(oferta.hashContinut ?? "").slice(0, 16)}…</code> {tr("— dovedește că ce se vede aici e exact ce s-a trimis.")}
              </p>
            </div>
          </div>
        )}

        <div className="modal-actiuni">
          <button className="buton buton-sters" onClick={laInchidere}>{tr("închide")}</button>
          {poateEmite && !ciorna && !oferta.inlocuitaDe && (
            <button className="buton" disabled={lucreaza}
              onClick={async () => {
                setLucreaza(true); setEroare("");
                try { await api.refaOferta(oferta.id); await laSchimbare(); laInchidere(); }
                catch (e) { setEroare(mesajEroare(e)); }
                finally { setLucreaza(false); }
              }}>
              {tr("refă ca ofertă nouă")}
            </button>
          )}
          {poateEmite && !ciorna && oferta.status !== "Acceptata" && (
            <button className="buton" disabled={lucreaza}
              onClick={async () => {
                setLucreaza(true); setEroare("");
                try {
                  const r = await api.trimiteOferta(oferta.id, { zileValabilitate: zile });
                  if (r.token) laLink(r.token);
                  await laSchimbare();
                } catch (e) { setEroare(mesajEroare(e)); }
                finally { setLucreaza(false); }
              }}>
              {tr("retrimite (link nou)")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function AdaugaLinie({ operatii, articole, lucreaza, laAdaugare }) {
  const [fel, setFel] = useState("manopera");
  const [id, setId] = useState("");
  const [cantitate, setCantitate] = useState(1);

  const optiuni = fel === "manopera" ? operatii : articole;

  return (
    <div className="linie-actiune" style={{ alignItems: "center" }}>
      <label>{tr("Fel")}
        <select className="camp" value={fel} onChange={(e) => { setFel(e.target.value); setId(""); }}>
          <option value="manopera">{tr("manoperă")}</option>
          <option value="material">{tr("piesă / material")}</option>
        </select>
      </label>
      <label style={{ flex: "1 1 18rem", minWidth: 0 }}>{tr("Poziție")}
        <select className="camp" style={{ width: "100%", minWidth: 0 }}
          value={id} onChange={(e) => setId(e.target.value)}>
          <option value="">{tr("— alege —")}</option>
          {optiuni.map((o) => (
            <option key={o.id} value={o.id}>{o.cod} — {tr(o.denumire)}</option>
          ))}
        </select>
      </label>
      <label>{tr("Cantitate")}
        <input className="camp" style={{ minWidth: "7rem", width: "7rem" }}
          type="number" min="0.0001" step="0.0001" value={cantitate}
          onChange={(e) => setCantitate(Number(e.target.value))} />
      </label>
      <button className="buton buton-mic buton-adauga" disabled={lucreaza || !id || !(cantitate > 0)}
        onClick={() => {
          laAdaugare(fel === "manopera"
            ? { operatieId: id, cantitate }
            : { articolId: id, cantitate });
          setId("");
        }}>
        {tr("adaugă linia")}
      </button>
    </div>
  );
}

/**
 * Programarea lucrării dintr-o ofertă acceptată.
 *
 * Stă aici, nu pe ecranul de intervenții, ca să nu fie nevoie să treci prin
 * două ecrane cu un identificator în buzunar. Vizita creată poartă `ofertaId`,
 * deci oferta iese din lista „acceptate, de programat" abia acum — nu la
 * acceptare, când încă nu convenise nimeni nicio dată.
 */
function ProgrameazaLucrarea({ oferta, laInchidere, laSalvare }) {
  const [locatii, setLocatii] = useState([]);
  const [locatieId, setLocatieId] = useState("");
  const [dataProgramata, setDataProgramata] = useState("");
  const [ora, setOra] = useState("");
  const [categorie, setCategorie] = useState("Extindere");
  const [eroare, setEroare] = useState("");
  const [lucreaza, setLucreaza] = useState(false);

  useEffect(() => {
    api.locatii(oferta.client?.id ?? oferta.clientId)
      .then(setLocatii)
      .catch((e) => setEroare(mesajEroare(e)));
  }, [oferta]);

  const lipsesc = [
    !locatieId && tr("locația"),
    !dataProgramata && tr("data"),
  ].filter(Boolean);

  async function trimite() {
    setLucreaza(true); setEroare("");
    try {
      await api.creeazaVizita({
        clientId: oferta.client?.id ?? oferta.clientId,
        locatieId,
        categorie,
        scopInterventie: `${oferta.numar} — ${oferta.titlu}`.slice(0, 500),
        dataProgramata: new Date(dataProgramata).toISOString(),
        ...(ora ? { oraProgramata: ora } : {}),
        ofertaId: oferta.id,
      });
      await laSalvare();
    } catch (e) { setEroare(mesajEroare(e)); }
    finally { setLucreaza(false); }
  }

  return (
    <div className="modal-fundal" onClick={laInchidere}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>{tr("Programează lucrarea")}</h3>
        <p className="subtitlu">
          {tr("Vizita se leagă de ofertă, deci oferta iese din lista celor de programat.")}
        </p>
        {eroare && <div className="eroare">{eroare}</div>}
        <div className="camp">
          <label htmlFor="pl-categorie">{tr("Fel de lucrare")}</label>
          <select id="pl-categorie" value={categorie} onChange={(e) => setCategorie(e.target.value)}>
            {Object.entries(CATEGORII_INTERVENTIE).map(([v, e]) => (
              <option key={v} value={v}>{tr(e)}</option>
            ))}
          </select>
        </div>
        <div className="camp">
          <label htmlFor="pl-locatie">{tr("Locație")}</label>
          <select id="pl-locatie" value={locatieId} onChange={(e) => setLocatieId(e.target.value)}>
            <option value="">{tr("alege…")}</option>
            {locatii.map((l) => <option key={l.id} value={l.id}>{tr(l.denumire)}</option>)}
          </select>
          {locatii.length === 0 && (
            <span className="nota-cautare">
              {tr("Clientul n-are nicio locație activă. Adaugă una din secțiunea Clienți — o vizită se programează la o adresă, nu la o firmă.")}
            </span>
          )}
        </div>
        <div className="formular-linie" style={{ gridTemplateColumns: "1fr 1fr" }}>
          <div className="camp">
            <label htmlFor="pl-data">{tr("Data")}</label>
            <input id="pl-data" type="date" value={dataProgramata}
              onChange={(e) => setDataProgramata(e.target.value)} />
          </div>
          <div className="camp">
            <label htmlFor="pl-ora">{tr("Ora (opțional)")}</label>
            <input id="pl-ora" type="time" value={ora} onChange={(e) => setOra(e.target.value)} />
          </div>
        </div>
        {lipsesc.length > 0 && (
          <p className="subtitlu">{tr("Completează:")} {lipsesc.join(", ")}.</p>
        )}
        <div className="modal-actiuni">
          <button className="buton buton-mic buton-sters" onClick={laInchidere}>{tr("renunță")}</button>
          <button className="buton buton-mic buton-actiune"
            disabled={lucreaza || lipsesc.length > 0} onClick={trimite}>
            {lucreaza ? tr("se creează…") : tr("creează vizita")}
          </button>
        </div>
      </div>
    </div>
  );
}
